// Web Push (VAPID) client helpers: capability detection, subscribe/unsubscribe, honest platform notes.
import { api } from "@/lib/api";
import { nativePlatform } from "@/lib/platform";

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const isStandalone = () => window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true || new URLSearchParams(window.location.search).get("src") === "twa";

export const pushCapability = () => {
  const native = nativePlatform();
  const hasApi = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  if (native === "ios") return { supported: false, reason: "ios_native", message: "Phone push for the iOS app needs Apple Push (APNs), which is not configured yet. In-app alerts still work while EV-IDEX is open." };
  if (!window.isSecureContext) return { supported: false, reason: "insecure", message: "Push requires a secure (https) connection." };
  if (isIOS() && !isStandalone()) return { supported: false, reason: "ios_browser", message: "On iPhone/iPad, push works only after you add EV-IDEX to your Home Screen (Share → Add to Home Screen, iOS 16.4+) and open it from there." };
  if (!hasApi) return { supported: false, reason: "unsupported", message: "This browser does not support background push notifications." };
  return { supported: true, reason: null, message: null };
};

export const permissionState = () => ("Notification" in window ? Notification.permission : "unsupported");

const decodeKey = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const s = (b64 + pad).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
};

const registration = async () => {
  const existing = await navigator.serviceWorker.getRegistration("/");
  return existing || navigator.serviceWorker.register("/sw.js");
};

export const currentSubscription = async () => {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.getRegistration("/");
  return reg ? reg.pushManager.getSubscription() : null;
};

// Must be called from a user gesture (tap) — browsers block unsolicited permission prompts.
export const enablePush = async () => {
  const cap = pushCapability();
  if (!cap.supported) throw new Error(cap.message);
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error(perm === "denied" ? "Notifications are blocked for EV-IDEX in your device/browser settings." : "Permission was not granted.");
  const reg = await registration();
  await navigator.serviceWorker.ready;
  const { publicKey } = (await api.get("/push/vapid-public-key")).data;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: decodeKey(publicKey) });
  const json = sub.toJSON();
  await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys, platform: nativePlatform() || (isStandalone() ? "pwa" : "web"), user_agent: navigator.userAgent, standalone: isStandalone() });
  return sub;
};

export const disablePush = async () => {
  const sub = await currentSubscription();
  if (!sub) return false;
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => {});
  await api.delete("/push/subscribe", { params: { endpoint } }).catch(() => {});
  return true;
};

// Routes SW messages (push received while open / tap-to-navigate fallback) into the app.
export const listenToServiceWorker = (navigate) => {
  if (!("serviceWorker" in navigator)) return () => {};
  const onMsg = (e) => {
    if (e.data?.type === "evidex:navigate" && e.data.url) {
      const u = new URL(e.data.url, window.location.origin);
      navigate(u.pathname + u.search);
    }
    if (e.data?.type === "evidex:push") window.dispatchEvent(new CustomEvent("apx:push-received", { detail: e.data.payload }));
  };
  navigator.serviceWorker.addEventListener("message", onMsg);
  return () => navigator.serviceWorker.removeEventListener("message", onMsg);
};
